'use client';

import { Switch as BaseSwitch } from '@base-ui-components/react/switch';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '../../utils/cn';
import './switch.css';

const switchVariants = cva(
  'switch-track peer inline-flex shrink-0 cursor-pointer items-center rounded-full border-2 border-transparent shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:cursor-not-allowed disabled:opacity-50 data-[checked]:bg-primary data-[unchecked]:bg-input',
  {
    variants: {
      size: {
        default: 'h-5 w-9',
        sm: 'h-4 w-7',
      },
    },
    defaultVariants: {
      size: 'default',
    },
  }
);

const thumbVariants = cva(
  'switch-thumb pointer-events-none block rounded-full bg-background shadow-sm',
  {
    variants: {
      size: {
        default: 'h-4 w-4 data-[checked]:translate-x-4',
        sm: 'h-3 w-3 data-[checked]:translate-x-3',
      },
    },
    defaultVariants: {
      size: 'default',
    },
  }
);

interface SwitchProps extends VariantProps<typeof switchVariants> {
  checked?: boolean;
  defaultChecked?: boolean;
  onCheckedChange?: (checked: boolean) => void;
  disabled?: boolean;
  required?: boolean;
  name?: string;
  className?: string;
  id?: string;
}

function Switch({
  className,
  size,
  checked,
  defaultChecked,
  onCheckedChange,
  ...props
}: SwitchProps & { ref?: React.Ref<HTMLElement> }) {
  const rootProps = {
    checked,
    defaultChecked,
    onCheckedChange,
    ...props,
  } as React.ComponentProps<typeof BaseSwitch.Root>;

  return (
    <BaseSwitch.Root
      className={cn(switchVariants({ size }), className)}
      {...rootProps}
    >
      <BaseSwitch.Thumb className={thumbVariants({ size })} />
    </BaseSwitch.Root>
  );
}

export { Switch, switchVariants };
export type { SwitchProps };
